import type { ParcelStatus } from '../api/types'
import { de } from '../i18n/de'
import { ROUTE_STEPS, STATUS_META, routePosition, type RouteTone } from '../lib/status'

const TONE: Record<RouteTone, string> = {
  today: 'bg-emerald-500 ring-emerald-500/25',
  pickup: 'bg-sky-500 ring-sky-500/25',
  problem: 'bg-red-500 ring-red-500/25',
}
const DONE = 'bg-emerald-600 dark:bg-emerald-500'
const OPEN = 'ring-1 ring-inset ring-slate-300 dark:ring-slate-600'

/**
 * The status as a route: one dot per step from announced to delivered, filled as far as
 * the parcel has come, the step it stands at drawn larger when that is news. Icon and word
 * stay with it, so the dots never have to be decoded.
 *
 * `reached` places a problem after the last step the parcel is known to have reached; only
 * the drawer has the history for it.
 */
export function StatusRoute({
  status,
  reached,
  size = 'sm',
}: {
  status: ParcelStatus
  reached?: ParcelStatus
  size?: 'sm' | 'lg'
}) {
  const meta = STATUS_META[status]
  const position = routePosition(status, reached)
  const steps = ROUTE_STEPS.map((_, step) => {
    const current = position.current?.step === step ? position.current.tone : null
    return { current, done: step < position.done && current == null }
  })
  const summary = de.route.steps
    .map((name, step) => `${name}: ${steps[step].done ? de.route.done : de.route.open}`)
    .join(' · ')

  if (size === 'lg') {
    return (
      <div className="w-full max-w-xs" title={summary}>
        <span className="sr-only">{meta.label}</span>
        <div className="flex items-center px-4" aria-hidden>
          {steps.map((s, step) => (
            <div key={step} className={`flex items-center ${step > 0 ? 'flex-1' : ''}`}>
              {step > 0 && (
                <span
                  className={`h-0.5 flex-1 ${
                    s.done || s.current
                      ? 'bg-emerald-600 dark:bg-emerald-500'
                      : 'bg-slate-200 dark:bg-slate-700'
                  }`}
                />
              )}
              <span
                className={`shrink-0 rounded-full ${
                  s.current
                    ? `h-3.5 w-3.5 ring-4 ${TONE[s.current]}`
                    : `h-2.5 w-2.5 ${s.done ? DONE : OPEN}`
                }`}
              />
            </div>
          ))}
        </div>
        <ol className="mt-1.5 grid grid-cols-4 text-center text-[11px] leading-tight" aria-hidden>
          {de.route.steps.map((name, step) => {
            const s = steps[step]
            return (
              <li
                key={name}
                className={
                  s.current
                    ? meta.text
                    : s.done
                      ? 'text-slate-600 dark:text-slate-300'
                      : 'text-slate-400 dark:text-slate-500'
                }
              >
                {/* Waiting at a pickup point or stuck: say so where the step would be. */}
                {s.current && s.current !== 'today' ? meta.label : name}
              </li>
            )
          })}
        </ol>
      </div>
    )
  }

  // Two short lines instead of one long one: the rows have height to spare, not width.
  const Icon = meta.icon
  return (
    <span className={`inline-flex shrink-0 flex-col gap-1 text-xs ${meta.text}`}>
      <span className="inline-flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {meta.label}
      </span>
      <span className="inline-flex items-center gap-[3px] pl-0.5" title={summary} aria-hidden>
        {steps.map((s, step) => (
          <span
            key={step}
            className={`shrink-0 rounded-full ${
              s.current
                ? `h-2 w-2 ring-2 ${TONE[s.current]}`
                : `h-1.5 w-1.5 ${s.done ? DONE : OPEN}`
            }`}
          />
        ))}
      </span>
    </span>
  )
}
